import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Pagination } from '../components/ui/pagination';
import { formatDate } from '../lib/utils';
import { useAuth } from '../hooks/useAuth';

const PAGE_SIZE = 20;

export function ServicesPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const location = useLocation();
  const isArchive = location.pathname === '/services/archive';
  const queryClient = useQueryClient();
  const [archiveModal, setArchiveModal] = useState<{ show: boolean; serviceId: string; serviceName: string }>({
    show: false,
    serviceId: '',
    serviceName: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['services', page, isArchive],
    queryFn: () => {
      const endpoint = isArchive ? '/api/services/archives/list' : '/api/services/list';
      return api
        .get(endpoint, { params: { offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE } })
        .then((r) => r.data);
    },
  });

  const { data: _activeData } = useQuery({
    queryKey: ['active-services-count'],
    queryFn: () => api.get('/api/services/list_active').then((r) => r.data),
  });

  const services = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  async function handleArchive(serviceInstanceId: string) {
    try {
      await api.post('/api/services/archive', { service_instance_id: serviceInstanceId });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['active-services-count'] });
      setArchiveModal({ show: false, serviceId: '', serviceName: '' });
    } catch (err) {
      console.error('Failed to archive service:', err);
    }
  }

  async function handleRestore(serviceInstanceId: string) {
    try {
      await api.post('/api/services/archives/restore', { service_instance_id: serviceInstanceId });
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['active-services-count'] });
    } catch (err) {
      console.error('Failed to restore service:', err);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-3">Hi {user?.firstname},</h1>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-4 flex border-b border-gray-200">
        <li className="nav-item">
          <Link
            to="/services"
            className={`nav-link px-4 py-2 -mb-px border-b-2 ${!isArchive ? 'text-[#cc0404] border-[#cc0404] font-medium' : 'text-[#767676] border-transparent hover:text-[#cc0404]'}`}
          >
            Active Services
          </Link>
        </li>
        <li className="nav-item">
          <Link
            to="/services/archive"
            className={`nav-link px-4 py-2 -mb-px border-b-2 ${isArchive ? 'text-[#cc0404] border-[#cc0404] font-medium' : 'text-[#767676] border-transparent hover:text-[#cc0404]'}`}
          >
            Archived Services
          </Link>
        </li>
        <li className="nav-item">
          <Link to="/services/catalog" className="nav-link px-4 py-2 text-[#767676] hover:text-[#cc0404] -mb-px border-b-2 border-transparent hover:border-[#cc0404]">
            Services Catalog
          </Link>
        </li>
      </ul>

      {/* Empty state */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading services...</div>
      ) : total === 0 ? (
        <Card className="rounded-[20px]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {isArchive ? 'No archived services!' : 'You have no active services!'}
              </h2>
              <img src="/assets/icon-action-graph.svg" alt="" className="w-16 h-16" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-[20px] mb-5">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-3">
              <h5 className="text-base font-semibold">
                {isArchive ? 'Archived Services' : 'My Active Services'}
              </h5>
            </div>

            {/* Table header */}
            <div className="grid grid-cols-12 gap-2 text-sm font-semibold text-[#767676] border-b pb-2 mb-2">
              <div className="col-span-1">&nbsp;</div>
              <div className="col-span-3">Service Name</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Created</div>
              <div className="col-span-2">Modified</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Table rows */}
            {services.map((service: any, index: number) => (
              <div key={service.service_instance_id}>
                <div className="grid grid-cols-12 gap-2 items-center py-4">
                  <div className="col-span-1 flex gap-1">
                    {service.source_app_logo && (
                      <img src={service.source_app_logo} alt="" width={32} height={32} className="rounded object-contain" />
                    )}
                    {service.target_app_logo && (
                      <img src={service.target_app_logo} alt="" width={32} height={32} className="rounded object-contain" />
                    )}
                  </div>
                  <div className="col-span-3">
                    <Link
                      to={`/services/details?id=${service.service_instance_id}`}
                      className="hover:underline"
                    >
                      {service.service_title}
                    </Link>
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      service.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-span-2 text-sm text-[#767676]">
                    {formatDate(service.created)}
                  </div>
                  <div className="col-span-2 text-sm text-[#767676]">
                    {formatDate(service.modified)}
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <Link
                      to={`/services/details?id=${service.service_instance_id}`}
                      className="text-[#767676] hover:text-[#cc0404]"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                    </Link>
                    {isArchive ? (
                      <button
                        className="text-[#767676] hover:text-green-600"
                        onClick={() => handleRestore(service.service_instance_id)}
                        title="Restore"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="text-[#767676] hover:text-[#cc0404]"
                        onClick={() => setArchiveModal({
                          show: true,
                          serviceId: service.service_instance_id,
                          serviceName: service.service_title,
                        })}
                        title="Archive"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                {index < services.length - 1 && (
                  <hr className="border-t border-gray-200 opacity-75" />
                )}
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Archive confirmation modal */}
      {archiveModal.show && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-[20px] p-6 max-w-md w-full mx-4 shadow-xl">
            <h5 className="text-lg font-semibold mb-4">Archive Confirmation</h5>
            <p className="mb-2">
              Are you sure you want to archive the service '{archiveModal.serviceName}'?
            </p>
            <p className="text-sm text-[#767676] mb-6">
              <b>Note:</b> Archived services can be restored from the Archived Services tab.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setArchiveModal({ show: false, serviceId: '', serviceName: '' })}>
                Close
              </Button>
              <Button onClick={() => handleArchive(archiveModal.serviceId)}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}